import { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Mail, Check, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NewsletterSignup = ({ source = 'blog', variant = 'inline' }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes('@')) {
      toast.error('Enter a valid email address');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/public/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source }),
      });
      const data = await res.json();
      if (res.ok) {
        setSubscribed(true);
        toast.success(data.message);
      } else {
        toast.error(data.detail || 'Failed to subscribe');
      }
    } catch {
      toast.error('Something went wrong');
    }
    setLoading(false);
  };

  if (subscribed) {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="flex items-center gap-2 justify-center p-4  bg-emerald-500/10 border border-emerald-500/20"
        data-testid="newsletter-subscribed"
      >
        <Check className="w-5 h-5 text-emerald-500" />
        <p className="text-sm font-medium text-emerald-500">You're subscribed! Check your inbox for study tips.</p>
      </motion.div>
    );
  }

  if (variant === 'card') {
    return (
      <div className="p-6 lg:p-8 bg-[#F0F3FB] border border-[#002FA7]/15" data-testid="newsletter-card">
        <div className="flex items-center gap-2 mb-2">
          <Mail className="w-4 h-4 text-[#002FA7]" strokeWidth={1.75} />
          <h3 className="text-lg font-bold text-[var(--isp-heading)]" style={{ fontFamily: 'var(--sf-display)' }}>Get Study Tips Weekly</h3>
        </div>
        <p className="text-sm text-[var(--isp-body)] mb-5" style={{ fontFamily: 'var(--sf-text)' }}>
          Join 1,000+ students receiving AI-powered study tips, exam strategies, and learning hacks every Tuesday and Thursday.
        </p>
        <form onSubmit={handleSubmit} className="flex gap-0">
          <Input
            type="email" value={email} onChange={e => setEmail(e.target.value)}
            placeholder="your@email.com"
            className="flex-1 rounded-none bg-white border-[#E4E4E7] text-[var(--isp-heading)]"
            data-testid="newsletter-email-input"
          />
          <Button type="submit" disabled={loading} className="rounded-none bg-[#002FA7] hover:bg-[#00227A] px-6 shrink-0" data-testid="newsletter-submit-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Subscribe'}
          </Button>
        </form>
        <p className="text-[10px] text-[var(--isp-muted)] mt-2">Free forever. Unsubscribe anytime.</p>
      </div>
    );
  }

  // Inline variant (for bottom of blog posts)
  return (
    <form onSubmit={handleSubmit} className="flex gap-0 max-w-md mx-auto" data-testid="newsletter-inline">
      <Input
        type="email" value={email} onChange={e => setEmail(e.target.value)}
        placeholder="your@email.com"
        className="flex-1 rounded-none bg-white border-[#E4E4E7] text-[var(--isp-heading)] px-5"
        data-testid="newsletter-email-input"
      />
      <Button type="submit" disabled={loading} className="rounded-none bg-[#002FA7] hover:bg-[#00227A] px-6 shrink-0" data-testid="newsletter-submit-btn">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Mail className="w-4 h-4 mr-1" /> Subscribe</>}
      </Button>
    </form>
  );
};

export default NewsletterSignup;
